import React from 'react';
import './MindAttentionLanding.css';

const MindAttentionLanding = () => {
  const features = [
    {
      icon: '🧠',
      title: 'CBT 기반 인지 훈련',
      description: '자동사고 탐지와 사고 재구성으로 ADHD 증상을 완화합니다',
      color: '#2D6A4F'
    },
    {
      icon: '🎯',
      title: '집중력 향상 도구',
      description: '포모도로 타이머와 작업 분할로 실행력을 개선합니다',
      color: '#52B788'
    },
    {
      icon: '🧘',
      title: '마인드풀니스',
      description: '짧은 명상과 호흡 가이드로 정서 안정을 도와줍니다',
      color: '#40916C'
    },
    {
      icon: '📊',
      title: '진행상황 추적',
      description: '일일 목표와 주간 스트릭으로 동기부여를 유지합니다',
      color: '#1B5E3F'
    }
  ];

  const benefits = [
    { icon: '⚡', text: '주의력 분산 최소화' },
    { icon: '✅', text: '즉각적인 피드백' },
    { icon: '⏱️', text: '시간 관리 개선' },
    { icon: '🎨', text: '개인화된 학습' },
    { icon: '🔄', text: '루틴 형성 지원' },
    { icon: '💪', text: '자기효능감 향상' }
  ];

  return (
    <div className="mind-attention-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">✨ ADHD 전문 인지 훈련</div>
          <h1 className="hero-title">
            당신의 집중력을 위한<br />
            <span className="gradient-text">Mind Attention</span>
          </h1>
          <p className="hero-description">
            CBT 기반 인지 훈련과 마인드풀니스로<br />
            ADHD 증상을 체계적으로 관리하세요
          </p>
          <div className="hero-buttons">
            <button className="btn-primary">
              무료 체험 시작하기
            </button>
            <button className="btn-secondary">
              자세히 알아보기
            </button>
          </div>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">10만+</span>
              <span className="stat-label">활성 사용자</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">4.8</span>
              <span className="stat-label">평균 평점</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">85%</span>
              <span className="stat-label">증상 개선</span>
            </div>
          </div>
        </div>
        <div className="hero-image">
          <div className="phone-mockup">
            <img src="/api/placeholder/300/600" alt="Mind Attention 앱 화면" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header">
          <h2>핵심 기능</h2>
          <p>ADHD 관리를 위한 종합적인 솔루션</p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card" style={{'--accent-color': feature.color}}>
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="benefits-content">
          <div className="benefits-text">
            <h2>왜 Mind Attention인가요?</h2>
            <p className="benefits-description">
              ADHD 전문가들과 함께 개발한 과학적 접근법으로
              실제 생활에서의 변화를 경험하세요
            </p>
            <div className="benefits-list">
              {benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <span className="benefit-icon">{benefit.icon}</span>
                  <span>{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="benefits-image">
            <img src="/api/placeholder/500/400" alt="Mind Attention 효과" />
          </div>
        </div>
      </section>

      {/* CBT Section */}
      <section className="cbt-section">
        <div className="cbt-content">
          <h2>CBT 기반 과학적 접근</h2>
          <p>인지행동치료의 검증된 기법을 모바일로 경험하세요</p>
          <div className="cbt-cards">
            <div className="cbt-card">
              <div className="cbt-number">01</div>
              <h3>자동사고 인식</h3>
              <p>부정적 사고 패턴을 발견하고 기록합니다</p>
            </div>
            <div className="cbt-card">
              <div className="cbt-number">02</div>
              <h3>인지 재구성</h3>
              <p>왜곡된 사고를 합리적으로 재구성합니다</p>
            </div>
            <div className="cbt-card">
              <div className="cbt-number">03</div>
              <h3>행동 실험</h3>
              <p>새로운 사고와 행동을 실생활에 적용합니다</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="testimonial-section">
        <h2>사용자 후기</h2>
        <div className="testimonials-grid">
          <div className="testimonial-card">
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p>"3개월 사용 후 집중력이 눈에 띄게 향상되었어요. 특히 포모도로 타이머가 정말 유용합니다."</p>
            <div className="testimonial-author">
              <strong>김민수</strong>
              <span>대학생, 23세</span>
            </div>
          </div>
          <div className="testimonial-card">
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p>"ADHD 진단 후 막막했는데, 이 앱이 큰 도움이 되었어요. CBT 훈련이 특히 효과적이었습니다."</p>
            <div className="testimonial-author">
              <strong>이서연</strong>
              <span>회사원, 28세</span>
            </div>
          </div>
          <div className="testimonial-card">
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p>"아이의 ADHD 관리에 정말 도움이 됩니다. 게임처럼 재미있어서 꾸준히 사용하고 있어요."</p>
            <div className="testimonial-author">
              <strong>박지영</strong>
              <span>학부모, 35세</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>지금 시작하세요</h2>
          <p>7일 무료 체험으로 변화를 경험해보세요</p>
          <div className="cta-buttons">
            <button className="btn-download">
              <span>🍎</span> App Store
            </button>
            <button className="btn-download">
              <span>🤖</span> Google Play
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>Mind Attention</h3>
            <p>ADHD를 위한 맞춤 인지 훈련 앱</p>
          </div>
          <div className="footer-section">
            <h4>제품</h4>
            <ul>
              <li><a href="#features">기능</a></li>
              <li><a href="#pricing">요금제</a></li>
              <li><a href="#faq">FAQ</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>회사</h4>
            <ul>
              <li><a href="#about">소개</a></li>
              <li><a href="#contact">문의</a></li>
              <li><a href="#blog">블로그</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>법적 고지</h4>
            <ul>
              <li><a href="/mindattention/terms">이용약관</a></li>
              <li><a href="/mindattention/privacy">개인정보처리방침</a></li>
              <li><a href="/mindattention/account-deletion">계정 삭제</a></li>
              <li><a href="/mindattention/toss">토스 서비스 이용약관</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2024 Mind Attention. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default MindAttentionLanding;
